import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Chip, Container, Grid, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { surveyAPI, userAPI } from '../services/api';

const OneToOnesCompleted: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selUser, setSelUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        // Load users for filter
        try {
          const u = await userAPI.getUsers();
          setUsers(u.data || []);
        } catch (_) {}
        const res = await surveyAPI.getSurveys();
        const all = (res.data || []) as any[];
        const is121 = (s: any) => Array.isArray(s.assignedUsers) && s.assignedUsers.length === 1 && (!Array.isArray(s.assignedTeams) || s.assignedTeams.length === 0);
        const now = new Date();
        const isCompleted = (s: any) => {
          const ended = !s.isActive || new Date(s.endDate) < now;
          const comp = s.completion || {};
          const reachedTarget = comp && comp.target > 0 && comp.responses >= comp.target;
          return ended || reachedTarget;
        };
        const list = all
          .filter((s) => !s.isTemplate)
          .filter(is121)
          .filter((s) => !s.archived)
          .filter(isCompleted)
          .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
        setRows(list);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load completed 1-2-1 surveys');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

  // Filter rows by selected employee (assigned user)
  const filtered = useMemo(() => {
    if (!selUser) return rows;
    const id = selUser._id;
    return rows.filter((s) => Array.isArray(s.assignedUsers) && s.assignedUsers.some((u: any) => (typeof u === 'string' ? u : u?._id) === id));
  }, [rows, selUser]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>Completed 1-2-1s</Typography>
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={users}
            getOptionLabel={(o: any) => o.name || o.email}
            value={selUser}
            onChange={(_, v) => setSelUser(v)}
            renderInput={(params) => <TextField {...params} label="Filter by employee" placeholder="All" />}
          />
        </Grid>
      </Grid>
      <Box sx={{ backgroundColor: 'white', borderRadius: 1, boxShadow: 1, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Responses</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">No completed 1-2-1 surveys</Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((s) => {
              const comp = s.completion || {};
              const pctRaw = typeof comp.rate === 'number' ? comp.rate * 100 : (comp.target ? (comp.responses || 0) / comp.target * 100 : undefined);
              const pct = Math.max(0, Math.min(100, Math.round(isNaN(pctRaw as any) ? 0 : (pctRaw as number))));
              const ended = !s.isActive || new Date(s.endDate) < new Date();
              const status = ended ? 'Ended' : 'Completed';
              const assignedUserId = Array.isArray(s.assignedUsers) && s.assignedUsers.length ? (typeof s.assignedUsers[0] === 'string' ? s.assignedUsers[0] : s.assignedUsers[0]?._id) : null;
              const employee = users.find((u) => u._id === assignedUserId);
              return (
                <TableRow key={s._id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{s.title}</TableCell>
                  <TableCell>{employee?.name || employee?.email || '—'}</TableCell>
                  <TableCell>{formatDate(s.startDate)} – {formatDate(s.endDate)}</TableCell>
                  <TableCell>
                    {typeof comp.responses === 'number' && typeof comp.target === 'number' && comp.target > 0
                      ? `${Math.min(comp.responses, comp.target)}/${comp.target}`
                      : `${pct}%`}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={status} color={ended ? 'default' : 'success'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(`/surveys/${s._id}/responses`)}>View results</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Container>
  );
};

export default OneToOnesCompleted;
